import { Text, type textVariants } from '@zealot/ui/components/text';
import type { VariantProps } from 'class-variance-authority';
import type * as React from 'react';

type TextVariants = VariantProps<typeof textVariants>;
type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

// gloss
const headingLevelConfig: Record<
  HeadingLevel,
  { element: React.ReactElement; size: TextVariants['size']; weight: TextVariants['weight'] }
> = {
  // biome-ignore lint/a11y/useHeadingContent: content is injected into <h1 /> by useRender's render prop at runtime
  1: { element: <h1 data-slot="heading" data-level={1} />, size: '2xl', weight: 'semibold' },
  // biome-ignore lint/a11y/useHeadingContent: content is injected into <h2 /> by useRender's render prop at runtime
  2: { element: <h2 data-slot="heading" data-level={2} />, size: 'xl', weight: 'semibold' },
  // biome-ignore lint/a11y/useHeadingContent: content is injected into <h3 /> by useRender's render prop at runtime
  3: { element: <h3 data-slot="heading" data-level={3} />, size: 'lg', weight: 'semibold' },
  // biome-ignore lint/a11y/useHeadingContent: content is injected into <h4 /> by useRender's render prop at runtime
  4: { element: <h4 data-slot="heading" data-level={4} />, size: 'sm', weight: 'medium' },
  // biome-ignore lint/a11y/useHeadingContent: content is injected into <h5 /> by useRender's render prop at runtime
  5: { element: <h5 data-slot="heading" data-level={5} />, size: 'xs', weight: 'medium' },
  // biome-ignore lint/a11y/useHeadingContent: content is injected into <h6 /> by useRender's render prop at runtime
  6: { element: <h6 data-slot="heading" data-level={6} />, size: 'xs', weight: 'medium' },
};

type HeadingProps = React.ComponentProps<'h1'> & {
  level: HeadingLevel;
  size?: TextVariants['size'];
  tone?: TextVariants['tone'];
  weight?: TextVariants['weight'];
  truncate?: boolean;
  lineClamp?: TextVariants['lineClamp'];
};

const Heading = ({ level, size, tone = 'default', weight, truncate, lineClamp, ...props }: HeadingProps) => {
  const config = headingLevelConfig[level];

  return (
    <Text
      render={config.element}
      size={size ?? config.size}
      weight={weight ?? config.weight}
      tone={tone}
      truncate={truncate}
      lineClamp={lineClamp}
      {...props}
    />
  );
};

export { Heading };
